import type { Benchmark } from '../../api';

/**
 * Filters of the benchmark catalog (AP-87): kind of value, new or existing
 * building, building type, cost group and level, unit, edition, region, and
 * free words. Pure, for the catalog and the picker in a cost plan.
 */

export type BenchmarkKind = 'Gebäude' | 'Bauelement' | 'Position' | 'sonstige';
export type BuildKind = 'Neubau' | 'Altbau' | '';

export interface BenchmarkFilter {
  words: string;
  kind: BenchmarkKind | null;
  build: BuildKind | null;
  buildingType: string | null;
  /** Leading digits, e.g. "3" or "33" or "331". */
  costGroup: string;
  /** 1 = 100, 2 = 310, 3 = 311, 4 = below (331.10 …). */
  level: 1 | 2 | 3 | 4 | null;
  unit: string | null;
  edition: string | null;
  region: string | null;
}

export const NO_FILTER: BenchmarkFilter = { words: '', kind: null, build: null, buildingType: null, costGroup: '', level: null, unit: null, edition: null, region: null };

/** What a BKI volume's title says about its values. */
export function kindOf(b: Pick<Benchmark, 'edition' | 'source'>): BenchmarkKind {
  const t = `${b.source} ${b.edition ?? ''}`;
  if (/Positionen/i.test(t)) return 'Position';
  if (/Bauelemente|Elemente/i.test(t)) return 'Bauelement';
  if (/Gebäude|Objekte/i.test(t)) return 'Gebäude';
  return 'sonstige';
}

export function buildOf(b: Pick<Benchmark, 'edition'>): BuildKind {
  const t = b.edition ?? '';
  if (/Altbau|Bestand|Sanierung/i.test(t)) return 'Altbau';
  if (/Neubau/i.test(t)) return 'Neubau';
  return '';
}

/** "m2 BGF" and "m² BGF" are the same unit; "St." and "St" too. */
export function normalUnit(unit: string | null): string {
  return (unit ?? '').trim().replace(/\bm2\b/g, 'm²').replace(/\bm3\b/g, 'm³').replace(/\bSt\.(?=\s|$)/g, 'St').replace(/\s+/g, ' ');
}

export function levelOf(code: string | null): 1 | 2 | 3 | 4 | null {
  if (!code) return null;
  if (!/^\d{3}$/.test(code)) return 4;
  if (code.endsWith('00')) return 1;
  if (code.endsWith('0')) return 2;
  return 3;
}

export function matches(b: Benchmark, f: BenchmarkFilter): boolean {
  if (f.kind && kindOf(b) !== f.kind) return false;
  if (f.build !== null && buildOf(b) !== f.build) return false;
  if (f.buildingType && b.buildingType !== f.buildingType) return false;
  if (f.costGroup && !(b.costGroup ?? '').startsWith(f.costGroup)) return false;
  if (f.level && levelOf(b.costGroup) !== f.level) return false;
  if (f.unit && normalUnit(b.unit) !== f.unit) return false;
  if (f.edition && b.edition !== f.edition) return false;
  if (f.region && b.region !== f.region) return false;
  const words = f.words.toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return true;
  const hay = [b.costGroup, b.label, b.buildingType, b.source, b.edition, b.region, b.priceDate, b.unit].join(' ').toLowerCase();
  return words.every((w) => hay.includes(w));
}

/** Choices for a filter, most frequent first, among the entries the other filters leave. */
export function options(list: readonly Benchmark[], pick: (b: Benchmark) => string | null): { value: string; label: string }[] {
  const count = new Map<string, number>();
  for (const b of list) {
    const v = pick(b);
    if (v) count.set(v, (count.get(v) ?? 0) + 1);
  }
  return [...count.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])).map(([value, n]) => ({ value, label: `${value} (${n})` }));
}

/** How many filters are set, words not counted. */
export function activeCount(f: BenchmarkFilter): number {
  return [f.kind, f.build, f.buildingType, f.costGroup || null, f.level, f.unit, f.edition, f.region].filter((x) => x !== null).length;
}

/**
 * Order for the picker of one cost line: the line's own cost group first,
 * then groups below it, then above; within that, the line's unit first.
 */
export function closeness(b: Pick<Benchmark, 'costGroup' | 'unit'>, costGroup: string, unit: string | null): number {
  const code = b.costGroup ?? '';
  const g = costGroup.split('.')[0]!;
  const place = code === costGroup ? 0 : code.startsWith(g) ? 1 : g.startsWith(code.replace(/0+$/, '')) ? 2 : 3;
  const sameUnit = unit !== null && normalUnit(b.unit) === normalUnit(unit) ? 0 : 1;
  return place * 2 + sameUnit;
}
